import PopupMenu from '@/components/pop-up-menu/pop-up-menu-component';
import ReactDOM from 'react-dom';
import FieldWithRevert from '@/components/react-hook-form-inputs/field-with-revert/field-with-revert-component';
import Tooltip from '@/components/tooltip/tooltip-component';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { CiFolderOn } from 'react-icons/ci';
import {
  MdFilterNone,
  MdOutlineChecklistRtl,
  MdOutlineListAlt,
  MdOutlinePlaylistAdd,
  MdOutlineRemoveRedEye,
  MdVisibilityOff,
} from 'react-icons/md';
import { useLocation } from 'react-router-dom';
import IsRequiredInput from './form-components/is-required-icon';
import { MultiSingleSelect } from './form-components/selection-type-component';
import {
  HasDependencies,
  styledOptionMenu as Styled,
} from './product-options-style';

const RenderDependencies = ({ item, name }) => {
  const { control } = useFormContext();
  const dependencies = useWatch({
    control,
    name: `${name}.dependencies`,
  });

  if (!dependencies || dependencies.length === 0) return null;

  return (
    <Tooltip
      text="This option has dependencies"
      id={item.id}
      icon={
        <HasDependencies $isHidden={item?.is_hidden}>
          <MdOutlineChecklistRtl color="inherit" />
        </HasDependencies>
      }
    />
  );
};

RenderDependencies.propTypes = {
  item: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
};

const OptionModalPortal = ({ children }) => {
  const tabRoot = document.getElementById('option-modal-portal');
  if (tabRoot) {
    return ReactDOM.createPortal(children, tabRoot);
  }
  return null;
};

OptionModalPortal.propTypes = {
  children: PropTypes.node.isRequired,
};
/**
 * Item component for displaying an option menu.
 * @param {object} props - The props passed to the OptionMenu component.
 * @param {Array} props.name - An array of menu items.
 * @param {Function} props.action - A function that handles the action that was clicked.
 * @param {object} props.item - The item object.
 * @param {Function} props.handleRevert - The handle revert function to use.
 * @returns {JSX.Element} The rendered Item component.
 */
const OptionRowForm = ({ name, action, item, handleRevert }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [canAdd, setCanAdd] = useState(true);
  const popupContainerRef = useRef(null);
  const location = useLocation();
  const isTemplatePage = location.pathname.includes('templates');

  const isLinkedToTemplate = !!item?.template_to_option_link_id || false;
  const isHidden = item?.is_hidden || false;

  const { getValues } = useFormContext();

  // Disable adding options into child options of dropdown folders
  const checkParentFolder = useCallback(() => {
    const parentFolderNameArray = name.split('.');

    if (parentFolderNameArray.length > 2) {
      const parentFolderItem = getValues(
        parentFolderNameArray.slice(0, 2).join('.'),
      );

      if (parentFolderItem?.select_type === 'dropdown') {
        setCanAdd(false);
      } else {
        setCanAdd(true);
      }
    } else {
      setCanAdd(true);
    }
  }, [getValues, name]);

  useEffect(() => {
    if (name) {
      checkParentFolder();
    }
  }, [checkParentFolder, name]);

  const [position, setPosition] = useState({ x: 0, y: 0 });

  const togglePopup = (e) => {
    if (!menuOpen) {
      // Calculate the distance to the bottom of the screen
      const distanceToBottom = window.innerHeight - e.clientY;

      const pos = e.target.getBoundingClientRect();
      const x = pos.left + pos.width;
      const y = pos.top + pos.height;
      setPosition({ x, y: distanceToBottom > 260 ? y : y - 260 });
    } else {
      setPosition({ x: 0, y: 0 });
    }
    setMenuOpen(!menuOpen);
  };

  const addOptionMenu = useMemo(
    () => [
      canAdd
        ? {
            label: 'Add Folder',
            icon: <CiFolderOn />,
            action: () => {
              action('folder');
            },
          }
        : null,
      canAdd
        ? {
            label: 'Add Option',
            icon: <MdOutlinePlaylistAdd />,
            action: () => {
              action('option');
            },
          }
        : null,
      {
        label: 'Edit Option',
        icon: <MdOutlinePlaylistAdd />,
        action: () => {
          action('editOption');
        },
      },
      {
        label: 'Duplicate Option',
        icon: <MdFilterNone />,
        action: () => {
          action('duplicateOption');
        },
      },
      !isTemplatePage && isHidden
        ? {
            label: 'Show',
            icon: <MdOutlineRemoveRedEye />,
            action: () => {
              action('show');
            },
          }
        : null,
      !isTemplatePage && !isHidden
        ? {
            label: 'Hide',
            icon: <MdVisibilityOff />,
            action: () => {
              action('hide');
            },
          }
        : null,
      // TODO: Currently in the designs but is a fallback, keep in for the meantime.
      // {
      //   label: 'Add Option Variant',
      //   icon: <MdOutlineRepeatOne />,
      //   action: () => {
      //     action('optionVariant');
      //   },
      //   tooltip: 'This option is not compatible with option variants',
      //   disabled: !item?.isVariant && item?.option_type === 'option',
      //   key: `option-variant-${item?.id}`,
      // },
      !isLinkedToTemplate
        ? {
            label: 'Delete',
            icon: <MdOutlineListAlt />,
            action: () => {
              action('delete');
            },
          }
        : null,
    ],
    [action, canAdd, isHidden, isLinkedToTemplate, isTemplatePage],
  );

  const addFolderMenu = [
    {
      label: 'Add Folder',
      icon: <CiFolderOn />,
      action: () => {
        action('folder');
      },
    },
    {
      label: 'Add Option',
      icon: <MdOutlinePlaylistAdd />,
      action: () => {
        action('option');
      },
    },
    {
      label: 'Duplicate Folder',
      icon: <MdFilterNone />,
      action: () => {
        action('duplicateOption');
      },
    },
    isHidden
      ? {
          label: 'Show',
          icon: <MdOutlineRemoveRedEye />,
          action: () => {
            action('show');
          },
        }
      : null,
    !isHidden
      ? {
          label: 'Hide',
          icon: <MdVisibilityOff />,
          action: () => {
            action('hide');
          },
        }
      : null,
    !isLinkedToTemplate
      ? {
          label: 'Delete',
          icon: <MdOutlineListAlt />,
          action: () => {
            action('delete');
          },
        }
      : null,
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the popup container
      const isOutsideContainer =
        popupContainerRef.current &&
        !popupContainerRef.current.contains(event.target);

      // Check if click is on a popup menu item (portal content)
      const isPopupMenuItem =
        event.target.closest('[data-popup-menu-item]') ||
        event.target.closest('.popup-menu-item') ||
        event.target.closest('[role="menuitem"]');

      // Only close if clicking outside AND not on a popup menu item
      if (isOutsideContainer && !isPopupMenuItem) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Styled.Container ref={popupContainerRef} id="option-row">
      {(item.children.length > 0 || item.option_type === 'folder') && (
        <MultiSingleSelect
          name={name}
          isHidden={item?.is_hidden}
          isFolder={item.option_type === 'folder'}
          disabled={isLinkedToTemplate}
        />
      )}
      <Styled.RevertHolder>
        <RenderDependencies item={item} name={name} />
        <FieldWithRevert
          fieldName={`productOptions.${item.template_to_option_link_id}.dependencies`}
          tooltipMessage="Revert Dependencies"
          inlineRevert
          handleRevert={(fieldName) =>
            handleRevert(fieldName, true, {
              formName: `${name}.dependencies`,
              templateOptionId: item.template_to_option_link_id,
              field: 'dependencies',
            })
          }
        />
      </Styled.RevertHolder>
      <IsRequiredInput
        name={name}
        $isHidden={item?.is_hidden}
        disabled={isLinkedToTemplate}
      />
      <Styled.MenuIcon className="icon menuDotsLabel" onClick={togglePopup} />

      {menuOpen && (
        <OptionModalPortal>
          <Styled.MenuRowWrapper>
            <PopupMenu
              isOpen={menuOpen}
              absolute
              top={`${position.y}`}
              left={`${position.x}`}
              onClose={() => setMenuOpen(false)}
              menuItems={
                item.option_type === 'folder'
                  ? addFolderMenu?.filter((menuItem) => menuItem !== null)
                  : addOptionMenu?.filter((menuItem) => menuItem !== null)
              }
            />
          </Styled.MenuRowWrapper>
        </OptionModalPortal>
      )}
    </Styled.Container>
  );
};

OptionRowForm.propTypes = {
  name: PropTypes.string.isRequired,
  action: PropTypes.func.isRequired,
  item: PropTypes.shape({
    children: PropTypes.array,
    dependencies: PropTypes.array,
    option_type: PropTypes.string,
    id: PropTypes.string,
    option_price_link_id: PropTypes.string,
    isVariant: PropTypes.bool,
    is_hidden: PropTypes.bool,
    template_to_option_link_id: PropTypes.string,
  }).isRequired,
  handleRevert: PropTypes.func,
};

OptionRowForm.defaultProps = {
  handleRevert: null,
};

export default OptionRowForm;
